/**
 * Advanced Error Tracking and Handling System
 * Provides comprehensive error monitoring, reporting, and user feedback
 */

import React from 'react';

class ErrorTracker {
  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.errorQueue = [];
    this.maxQueueSize = 50;
    this.flushInterval = 5000; // 5 seconds

    this.initializeErrorTracking();
    this.initializeRetryMechanism();
  }

  initializeErrorTracking() {
    // Global error handlers
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));

    // Start periodic error reporting
    if (this.isProduction) {
      setInterval(() => this.flushErrors(), this.flushInterval);
    }
  }

  initializeRetryMechanism() {
    // Retry failed error reports every 60 seconds
    setInterval(() => {
      this.retryFailedReports();
    }, 60000);
    
    // Also retry when page becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.retryFailedReports();
      }
    });
    
    // Retry when network connection is restored
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        if (navigator.connection.effectiveType !== 'slow-2g') {
          this.retryFailedReports();
        }
      });
    }
  }

  handleGlobalError(event) {
    const error = {
      type: 'javascript',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };

    this.captureError(error);
  }

  handleUnhandledRejection(event) {
    const error = {
      type: 'unhandled-promise',
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };

    this.captureError(error);
  }

  captureError(error, context = {}) {
    // Enhanced error object with context
    const enhancedError = {
      ...error,
      context: {
        ...context,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        performance: this.getPerformanceMetrics(),
        memory: this.getMemoryInfo(),
        connection: this.getConnectionInfo()
      },
      severity: this.determineSeverity(error),
      fingerprint: this.generateErrorFingerprint(error)
    };

    // Add to queue for batch processing
    this.errorQueue.push(enhancedError);

    // Limit queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Immediate handling for critical errors
    if (enhancedError.severity === 'critical') {
      this.handleCriticalError(enhancedError);
    }

    // Log in development
    if (!this.isProduction) {
      console.error('Error Tracked:', enhancedError);
    }
  }

  determineSeverity(error) {
    // Critical errors that break core functionality
    const criticalPatterns = [
      /authentication/i,
      /payment/i,
      /database/i,
      /network request failed/i,
      /chunk load error/i
    ];

    // High severity errors that impact user experience
    const highPatterns = [
      /component/i,
      /render/i,
      /navigation/i,
      /form/i
    ];

    const message = error.message || '';

    if (criticalPatterns.some(pattern => pattern.test(message))) {
      return 'critical';
    } else if (highPatterns.some(pattern => pattern.test(message))) {
      return 'high';
    } else {
      return 'medium';
    }
  }

  generateErrorFingerprint(error) {
    // Create unique fingerprint for error deduplication
    const key = `${error.message}-${error.filename}-${error.lineno}`;
    return btoa(key).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  }

  getPerformanceMetrics() {
    if (!window.performance) return null;

    const navigation = performance.getEntriesByType('navigation')[0];
    return {
      loadTime: navigation?.loadEventEnd - navigation?.loadEventStart,
      domContentLoaded: navigation?.domContentLoadedEventEnd - navigation?.domContentLoadedEventStart,
      firstContentfulPaint: this.getFCPMetric(),
      largestContentfulPaint: this.getLCPMetric()
    };
  }

  getFCPMetric() {
    try {
      const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0];
      return fcpEntry ? fcpEntry.startTime : null;
    } catch (e) {
      return null;
    }
  }

  getLCPMetric() {
    try {
      return new Promise((resolve) => {
        new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          resolve(lastEntry ? lastEntry.startTime : null);
        }).observe({ entryTypes: ['largest-contentful-paint'] });
      });
    } catch (e) {
      return null;
    }
  }

  getMemoryInfo() {
    if (!performance.memory) return null;

    return {
      usedJSHeapSize: performance.memory.usedJSHeapSize,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
    };
  }

  getConnectionInfo() {
    if (!navigator.connection) return null;

    return {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt,
      saveData: navigator.connection.saveData
    };
  }

  getCurrentUserId() {
    // Extract from auth context or local storage
    try {
      const authData = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return authData.id || 'anonymous';
    } catch (e) {
      return 'anonymous';
    }
  }

  getCurrentTenantId() {
    // Extract from tenant context or local storage
    try {
      const tenantData = JSON.parse(localStorage.getItem('tenant_context') || '{}');
      return tenantData.id || 'unknown';
    } catch (e) {
      return 'unknown';
    }
  }

  handleCriticalError(error) {
    // Immediate user notification for critical errors
    this.showUserNotification(error);

    // Immediate reporting for critical errors
    this.reportErrorImmediately(error);
  }

  showUserNotification(error) {
    // Import error dictionary dynamically to get user-friendly message
    import('./errorDictionary.js').then(({ getErrorMessage }) => {
      const errorDetails = getErrorMessage(error.message || 'GEN_001');

      // Create user-friendly error notification
      const notification = document.createElement('div');
      notification.className = 'error-notification';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 400px;
        font-family: system-ui, -apple-system, sans-serif;
        animation: slideIn 0.3s ease-out;
      `;

      // Create notification content with recovery actions
      const recoveryButtons = errorDetails.recoveryActions
        ? errorDetails.recoveryActions.map((action, index) => `
            <button
              data-action="${action.action}"
              data-route="${action.route || ''}"
              style="
                background: rgba(255,255,255,0.2);
                border: 1px solid rgba(255,255,255,0.3);
                color: white;
                padding: 6px 12px;
                border-radius: 4px;
                font-size: 13px;
                cursor: pointer;
                margin-right: 8px;
                margin-top: 8px;
                transition: background 0.2s;
              "
              onmouseover="this.style.background='rgba(255,255,255,0.3)'"
              onmouseout="this.style.background='rgba(255,255,255,0.2)'"
            >
              ${action.label}
            </button>
          `).join('')
        : '';

      notification.innerHTML = `
        <div style="padding-right: 24px;">
          <div style="font-weight: 600; margin-bottom: 4px; display: flex; align-items: center; gap: 8px;">
            ${errorDetails.title}
            <code style="font-size: 10px; background: rgba(0,0,0,0.2); padding: 2px 4px; border-radius: 3px;">
              ${errorDetails.code}
            </code>
          </div>
          <div style="font-size: 14px; opacity: 0.9; margin-bottom: 4px;">
            ${errorDetails.message}
          </div>
          ${errorDetails.suggestion ? `
            <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">
              💡 ${errorDetails.suggestion}
            </div>
          ` : ''}
          ${recoveryButtons ? `<div style="margin-top: 12px;">${recoveryButtons}</div>` : ''}
        </div>
        <button onclick="this.parentElement.remove()" style="
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          position: absolute;
          top: 8px;
          right: 8px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.2s;
        " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">
          ×
        </button>
      `;

      document.body.appendChild(notification);

      // Add event listeners for recovery actions
      if (errorDetails.recoveryActions) {
        notification.querySelectorAll('[data-action]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const action = e.target.getAttribute('data-action');
            const route = e.target.getAttribute('data-route');

            switch (action) {
              case 'retry':
                window.location.reload();
                break;
              case 'refresh':
                window.location.reload();
                break;
              case 'go-back':
                window.history.back();
                break;
              default:
                if (route) {
                  window.location.href = route;
                }
            }

            notification.remove();
          });
        });
      }

      // Auto-remove after 10 seconds (longer for errors with actions)
      const duration = errorDetails.recoveryActions?.length > 0 ? 15000 : 10000;
      setTimeout(() => {
        if (notification.parentNode) {
          notification.style.animation = 'slideOut 0.3s ease-in';
          setTimeout(() => notification.remove(), 300);
        }
      }, duration);
    }).catch(() => {
      // Fallback if error dictionary can't be loaded
      console.error('Failed to load error dictionary');
    });
  }

  async reportErrorImmediately(error) {
    try {
      // In production, this would send to error tracking service
      if (this.isProduction) {
        // Try our new monitoring endpoint first
        try {
          await fetch('/api/monitoring/alerts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify({
              ...error,
              severity: 'critical',
              source: 'error_tracker_immediate'
            })
          });
        } catch (monitoringError) {
          console.warn('Failed to send to monitoring endpoint, trying fallback:', monitoringError);
          
          // Fallback to the original endpoint
          await fetch('/api/errors', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify(error)
          });
        }
      }
    } catch (e) {
      console.warn('Failed to report error immediately:', e);
      // Store locally for retry
      this.storeForRetry(error);
    }
  }

  async flushErrors() {
    if (this.errorQueue.length === 0) return;

    const errorsToFlush = [...this.errorQueue];
    this.errorQueue = [];

    try {
      if (this.isProduction) {
        // Try our new monitoring endpoint first
        try {
          await fetch('/api/monitoring/alerts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify({
              alerts: errorsToFlush,
              source: 'error_tracker_batch'
            })
          });
        } catch (monitoringError) {
          console.warn('Failed to send batch to monitoring endpoint, trying fallback:', monitoringError);
          
          // Fallback to the original endpoint
          await fetch('/api/errors/batch', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify(errorsToFlush)
          });
        }
      }
    } catch (e) {
      console.warn('Failed to flush error queue:', e);
      // Re-add errors to queue for retry
      this.errorQueue.unshift(...errorsToFlush);
      // Also store in localStorage for persistence
      this.storeForRetry(errorsToFlush);
    }
  }

  /**
   * Store errors for later retry
   */
  storeForRetry(errors) {
    try {
      const failedErrors = JSON.parse(localStorage.getItem('failed_error_reports') || '[]');
      const errorsArray = Array.isArray(errors) ? errors : [errors];
      
      failedErrors.push(...errorsArray.map(error => ({
        ...error,
        timestamp: new Date().toISOString(),
        retryCount: 0
      })));
      
      // Keep only last 50 failed errors
      if (failedErrors.length > 50) {
        failedErrors.splice(0, failedErrors.length - 50);
      }
      
      localStorage.setItem('failed_error_reports', JSON.stringify(failedErrors));
    } catch (e) {
      console.error('Failed to store errors for retry:', e);
    }
  }

  /**
   * Retry failed error reports
   */
  async retryFailedReports() {
    try {
      const failedErrors = JSON.parse(localStorage.getItem('failed_error_reports') || '[]');
      const remainingErrors = [];

      for (const error of failedErrors) {
        if (error.retryCount >= 3) {
          // Skip errors that have been retried too many times
          continue;
        }

        try {
          await fetch('/api/monitoring/alerts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify(error)
          });

          console.log('Retried error report sent successfully');
        } catch (retryError) {
          // Increment retry count and keep for next time
          error.retryCount++;
          remainingErrors.push(error);
        }
      }

      localStorage.setItem('failed_error_reports', JSON.stringify(remainingErrors));
    } catch (e) {
      console.error('Failed to retry error reports:', e);
    }
  }

  // Public API methods
  captureException(error, context) {
    this.captureError({
      type: 'manual',
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    }, context);
  }

  captureMessage(message, level = 'info', context) {
    this.captureError({
      type: 'message',
      message,
      level,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    }, context);
  }

  setUserContext(user) {
    this.userContext = user;
  }

  setTenantContext(tenant) {
    this.tenantContext = tenant;
  }
}

// Create global instance
const errorTracker = new ErrorTracker();

// Export for use in components
export default errorTracker;

// Utility functions for React components
export const withErrorBoundary = (WrappedComponent) => {
  return class ErrorBoundaryWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      errorTracker.captureException(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      });
    }

    render() {
      if (this.state.hasError) {
        return (
          <div className="error-fallback">
            <h2>Something went wrong</h2>
            <p>We've been notified of this issue and are working to fix it.</p>
            <button onClick={() => window.location.reload()}>
              Refresh Page
            </button>
          </div>
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };
};

export const useErrorHandler = () => {
  return {
    captureException: (error, context) => errorTracker.captureException(error, context),
    captureMessage: (message, level, context) => errorTracker.captureMessage(message, level, context)
  };
};
